import { DEFAULT_BASEMAP_ID, DEFAULT_VIEW_MODE } from './basemaps';
import type { MapViewMode } from './basemaps';
import type { FeatureOverlayState, PoiTypeState } from './mapLayers';
import { DEFAULT_FEATURE_OVERLAY_STATE, DEFAULT_POI_TYPE_STATE } from './mapLayers';

export const MAP_PREFERENCES_KEY = 'orbit.map.preferences.v1';

export interface MapViewport {
    longitude: number;
    latitude: number;
    zoom: number;
    bearing: number;
    pitch: number;
}

export interface MapPreferences {
    selectedBasemapId: string;
    viewMode: MapViewMode;
    featureOverlays: FeatureOverlayState;
    poiTypes: PoiTypeState;
    viewport?: MapViewport;
}

function getDefaultPreferences(): MapPreferences {
    return {
        selectedBasemapId: DEFAULT_BASEMAP_ID,
        viewMode: DEFAULT_VIEW_MODE,
        featureOverlays: { ...DEFAULT_FEATURE_OVERLAY_STATE },
        poiTypes: { ...DEFAULT_POI_TYPE_STATE },
    };
}

/**
 * Merges stored overlay values onto the current defaults.
 * - Known keys present in stored data override the default.
 * - Keys absent from stored data (newly added layers) keep their configured default.
 * - Unknown stored keys (removed/renamed layers) are ignored.
 * - Non-boolean stored values are ignored.
 *
 * Exported for the v2 preferences module, which applies the same per-field tolerance.
 */
export function mergeOverlayState<K extends string>(defaults: Record<K, boolean>, stored: unknown): Record<K, boolean> {
    if (typeof stored !== 'object' || stored === null) return { ...defaults };
    const result = { ...defaults };
    for (const key of Object.keys(defaults) as K[]) {
        const value = (stored as Record<string, unknown>)[key];
        if (typeof value === 'boolean') {
            result[key] = value;
        }
    }
    return result;
}

/** Exported for the v2 preferences module, which applies the same viewport validation. */
export function validateViewport(stored: unknown): MapViewport | undefined {
    if (typeof stored !== 'object' || stored === null) return undefined;
    const v = stored as Record<string, unknown>;
    const { longitude, latitude, zoom, bearing, pitch } = v;

    if (typeof longitude !== 'number' || longitude < -180 || longitude > 180) return undefined;
    if (typeof latitude !== 'number' || latitude < -90 || latitude > 90) return undefined;
    if (typeof zoom !== 'number' || zoom < 0 || zoom > 24) return undefined;

    return {
        longitude,
        latitude,
        zoom,
        bearing: typeof bearing === 'number' && bearing >= -180 && bearing <= 180 ? bearing : 0,
        pitch: typeof pitch === 'number' && pitch >= 0 && pitch <= 85 ? pitch : 0,
    };
}

function parseRaw(raw: string): Record<string, unknown> | null {
    try {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return null;
        if ((parsed as Record<string, unknown>)['version'] !== 1) return null;
        return parsed as Record<string, unknown>;
    } catch {
        return null;
    }
}

/**
 * Loads and validates map preferences from localStorage.
 * Falls back to configured defaults on any error or missing/invalid data.
 * Merges stored overlay state so newly added layer keys always use their configured default.
 */
export function loadMapPreferences(validBasemapIds: string[]): MapPreferences {
    try {
        const raw = localStorage.getItem(MAP_PREFERENCES_KEY);
        if (!raw) return getDefaultPreferences();

        const stored = parseRaw(raw);
        if (!stored) return getDefaultPreferences();

        const storedBasemapId = stored['selectedBasemapId'];
        const selectedBasemapId =
            typeof storedBasemapId === 'string' && validBasemapIds.includes(storedBasemapId)
                ? storedBasemapId
                : DEFAULT_BASEMAP_ID;

        const storedViewMode = stored['viewMode'];
        const viewMode: MapViewMode =
            storedViewMode === '2d' || storedViewMode === '3d' || storedViewMode === 'globe'
                ? storedViewMode
                : DEFAULT_VIEW_MODE;

        return {
            selectedBasemapId,
            viewMode,
            featureOverlays: mergeOverlayState(DEFAULT_FEATURE_OVERLAY_STATE, stored['featureOverlays']),
            poiTypes: mergeOverlayState(DEFAULT_POI_TYPE_STATE, stored['poiTypes']),
            viewport: validateViewport(stored['viewport']),
        };
    } catch {
        return getDefaultPreferences();
    }
}

/** Saves map preferences to localStorage. Silently ignores write failures. */
export function saveMapPreferences(prefs: MapPreferences): void {
    try {
        localStorage.setItem(MAP_PREFERENCES_KEY, JSON.stringify({ version: 1, ...prefs }));
    } catch {
        // localStorage may be unavailable (private browsing, storage quota, etc.)
    }
}
